import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { format } from 'date-fns';
import {
  Plus,
  Package,
  ClipboardText,
  CheckCircle,
  Clock,
  Warning,
  Spinner,
  XCircle,
  Trash,
  ArrowsClockwise,
  Eye
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const InternalRequesterPortalPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({ stats: {}, recent_requisitions: [] });
  const [requisitions, setRequisitions] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [showNewReqDialog, setShowNewReqDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedReq, setSelectedReq] = useState(null);
  
  // New requisition form state
  const [newReqItems, setNewReqItems] = useState([]);
  const [reqRequiredDate, setReqRequiredDate] = useState('');
  const [reqNotes, setReqNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Get tab-specific storage key for session isolation
  const getTabStorageKey = () => {
    let tabId = window.__HERCHAIN_TAB_ID__;
    if (!tabId) {
      tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      window.__HERCHAIN_TAB_ID__ = tabId;
    }
    return `herchain_session_${tabId}`;
  };

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem(getTabStorageKey());
    return { Authorization: `Bearer ${token}` };
  };

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/internal-requester-portal/dashboard`, { headers: getAuthHeaders() });
      setDashboard(res.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const fetchRequisitions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/internal-requisitions`, { headers: getAuthHeaders() });
      setRequisitions(res.data);
    } catch (error) {
      console.error('Failed to fetch requisitions:', error);
    }
  };

  const fetchAvailableItems = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/internal-requester-portal/items`, { headers: getAuthHeaders() });
      setAvailableItems(res.data);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchDashboard(), fetchRequisitions(), fetchAvailableItems()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const addItemToReq = (item) => {
    if (newReqItems.find(i => i.item_id === item.id)) {
      toast.error('Item already added');
      return;
    }
    setNewReqItems([...newReqItems, {
      item_id: item.id,
      item_name: item.item_name,
      quantity_requested: 1,
      unit: item.unit_of_measure || 'Unit',
      current_stock: item.current_stock_level
    }]);
  };

  const updateItemQty = (itemId, qty) => {
    setNewReqItems(newReqItems.map(i => i.item_id === itemId ? { ...i, quantity_requested: parseInt(qty) || 1 } : i));
  };

  const removeItem = (itemId) => {
    setNewReqItems(newReqItems.filter(i => i.item_id !== itemId));
  };

  const submitRequisition = async () => {
    if (newReqItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    if (!reqRequiredDate) {
      toast.error('Please select a required date');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/internal-requisitions`, {
        items: newReqItems.map(i => ({
          item_id: i.item_id,
          item_name: i.item_name,
          quantity_requested: i.quantity_requested,
          unit: i.unit
        })),
        required_date: reqRequiredDate,
        notes: reqNotes
      }, { headers: getAuthHeaders() });

      toast.success('Internal requisition submitted successfully');
      setShowNewReqDialog(false);
      setNewReqItems([]);
      setReqRequiredDate('');
      setReqNotes('');
      fetchAllData();
    } catch (error) {
      console.error('Failed to submit requisition:', error);
      toast.error('Failed to submit requisition');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      fulfilled: 'bg-green-100 text-green-700',
      partially_fulfilled: 'bg-amber-100 text-amber-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const labels = {
      submitted: 'Pending',
      in_progress: 'In Progress',
      fulfilled: 'Fulfilled',
      partially_fulfilled: 'Partially Fulfilled',
      rejected: 'Rejected'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'fulfilled': return <CheckCircle size={20} weight="fill" className="text-green-500" />;
      case 'partially_fulfilled': return <Warning size={20} weight="fill" className="text-amber-500" />;
      case 'submitted': return <Clock size={20} weight="fill" className="text-blue-500" />;
      case 'in_progress': return <Spinner size={20} className="text-purple-500" />;
      case 'rejected': return <XCircle size={20} weight="fill" className="text-red-500" />;
      default: return <Clock size={20} className="text-slate-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#EF4444]/10 flex items-center justify-center">
            <ClipboardText size={24} className="text-[#EF4444]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Internal Requester Portal
            </h1>
            <p className="text-slate-500">Welcome, {user?.full_name}</p>
          </div>
        </div>
      </div>

      {/* Key Actions - 2 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Dialog open={showNewReqDialog} onOpenChange={setShowNewReqDialog}>
          <DialogTrigger asChild>
            <div className="bg-gradient-to-br from-[#EF4444] to-[#F87171] rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-all" data-testid="action-request-items">
              <Plus size={32} className="mb-3" />
              <h3 className="text-xl font-bold">Request Items</h3>
              <p className="text-white/80 text-sm mt-1">Submit a new internal requisition</p>
            </div>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Internal Requisition</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Item Selection */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Select Items from Inventory</Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-2 bg-slate-50">
                  {availableItems.map(item => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-2 bg-white rounded border hover:border-[#3D1D72] cursor-pointer"
                      onClick={() => addItemToReq(item)}
                      data-testid={`select-item-${item.id}`}
                    >
                      <div>
                        <p className="text-sm font-medium">{item.item_name}</p>
                        <p className="text-xs text-slate-500">{item.category} • Stock: {item.current_stock_level}</p>
                      </div>
                      <Plus size={16} className="text-[#3D1D72]" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Selected Items */}
              {newReqItems.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Items to Request</Label>
                  <div className="space-y-2">
                    {newReqItems.map(item => (
                      <div key={item.item_id} className="flex items-center gap-3 p-3 bg-[#F0EBF8] rounded-lg">
                        <Package size={20} className="text-[#3D1D72]" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.item_name}</p>
                          <p className="text-xs text-slate-500">Available: {item.current_stock} {item.unit}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity_requested}
                            onChange={(e) => updateItemQty(item.item_id, e.target.value)}
                            className="w-20"
                            data-testid={`qty-input-${item.item_id}`}
                          />
                          <span className="text-sm text-slate-500">{item.unit}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeItem(item.item_id)}>
                            <Trash size={16} className="text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Required Date */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Required Date</Label>
                <Input
                  type="date"
                  value={reqRequiredDate}
                  onChange={(e) => setReqRequiredDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="required-date-input"
                />
              </div>

              {/* Notes */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Notes (Optional)</Label>
                <Textarea
                  value={reqNotes}
                  onChange={(e) => setReqNotes(e.target.value)}
                  placeholder="Add any special instructions..."
                  rows={2}
                  data-testid="notes-input"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowNewReqDialog(false)}>Cancel</Button>
                <Button
                  onClick={submitRequisition}
                  disabled={submitting || newReqItems.length === 0}
                  className="bg-[#3D1D72] hover:bg-[#2d1555]"
                  data-testid="submit-requisition-btn"
                >
                  {submitting ? <Spinner size={16} className="animate-spin mr-2" /> : null}
                  Submit Requisition
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <Eye size={32} className="text-[#EF4444] mb-3" />
          <h3 className="text-xl font-bold text-slate-800">Track Requests</h3>
          <p className="text-slate-500 text-sm mt-1">{dashboard.stats?.submitted || 0} pending, {dashboard.stats?.fulfilled || 0} fulfilled</p>
        </div>
      </div>

      {/* Requisitions Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-white rounded-lg">
            Recent Requisitions
          </TabsTrigger>
          <TabsTrigger value="all" className="data-[state=active]:bg-white rounded-lg">
            All Requisitions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="recent-requisitions">
            <div className="p-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-800">Recent Internal Requisitions</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {loading ? (
                <div className="p-12 text-center text-slate-500">
                  <Spinner size={24} className="animate-spin mx-auto mb-2" />
                  Loading...
                </div>
              ) : dashboard.recent_requisitions?.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <ClipboardText size={48} className="mx-auto mb-3 text-slate-300" />
                  <p>No internal requisitions yet</p>
                  <p className="text-sm">Create your first requisition to get started</p>
                </div>
              ) : (
                dashboard.recent_requisitions?.map(req => (
                  <div key={req.id} className="p-4 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedReq(req)} data-testid={`requisition-${req.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(req.status)}
                        <div>
                          <p className="font-medium text-slate-800">{req.requisition_number}</p>
                          <p className="text-sm text-slate-500">{req.items?.length} item(s) • Required: {format(new Date(req.required_date), 'MMM d, yyyy')}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(req.status)}
                        <p className="text-xs text-slate-400 mt-1">{format(new Date(req.created_at), 'MMM d, yyyy')}</p>
                      </div>
                    </div>
                    {req.status === 'partially_fulfilled' && (
                      <div className="mt-2 p-2 bg-amber-50 rounded text-sm text-amber-700">
                        <Warning size={14} className="inline mr-1" />
                        Some items had shortfalls
                      </div>
                    )}
                    {req.status === 'rejected' && req.rejection_reason && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                        <XCircle size={14} className="inline mr-1" />
                        Rejected: {req.rejection_reason}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="all" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="all-requisitions">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#3D1D72] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Requisition #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Items</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Required By</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Issued By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                      <Spinner size={24} className="animate-spin mx-auto mb-2" />Loading...
                    </td></tr>
                  ) : requisitions.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No requisitions found</td></tr>
                  ) : (
                    requisitions.map((req, idx) => (
                      <tr key={req.id} className={`hover:bg-slate-50 ${idx % 2 === 0 ? 'bg-[#F0EBF8]' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-[#3D1D72]">{req.requisition_number}</td>
                        <td className="px-4 py-3 text-slate-600">{format(new Date(req.created_at), 'MMM d, yyyy')}</td>
                        <td className="px-4 py-3 text-slate-600">{req.items?.length} item(s)</td>
                        <td className="px-4 py-3 text-slate-600">{format(new Date(req.required_date), 'MMM d, yyyy')}</td>
                        <td className="px-4 py-3">{getStatusBadge(req.status)}</td>
                        <td className="px-4 py-3 text-slate-600">{req.issued_by_name || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      {selectedReq && (
        <Dialog open={!!selectedReq} onOpenChange={() => setSelectedReq(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Requisition {selectedReq.requisition_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Status</span>
                {getStatusBadge(selectedReq.status)}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Department</span>
                <span className="font-medium">{selectedReq.department}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Required Date</span>
                <span className="font-medium">{format(new Date(selectedReq.required_date), 'MMM d, yyyy')}</span>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Items</h4>
                <div className="space-y-2">
                  {selectedReq.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <span>{item.item_name}</span>
                      <div className="text-right">
                        <span className="font-medium">{item.quantity_issued || item.quantity_requested} {item.unit}</span>
                        {item.quantity_issued && item.quantity_issued < item.quantity_requested && (
                          <span className="text-xs text-amber-600 block">Requested: {item.quantity_requested}</span>
                        )}
                        {item.shortfall_note && (
                          <span className="text-xs text-red-600 block">{item.shortfall_note}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {selectedReq.issued_by_name && (
                <div className="flex justify-between items-center border-t pt-4">
                  <span className="text-sm text-slate-500">Issued By</span>
                  <span className="font-medium">{selectedReq.issued_by_name}</span>
                </div>
              )}
              
              {selectedReq.rejection_reason && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                  <p className="text-sm text-red-600">{selectedReq.rejection_reason}</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default InternalRequesterPortalPage;
